import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { ToastService } from '../../../base/services/toast.service';
import { SpinnerService } from '../../../base/spinner/spinner.service';
import {
  CategoryClient,
  CategoryDTO,
  CreateCategoryDTO,
  UpdateCategoryDTO,
} from '../../../base/models/models.swagger.generated';
import { ErrorType } from '../../../core/routing/error-types';
import { IPagedItems } from 'src/app/base/shared/table/paged-items.interface';
import { PagedQueryModel } from '../../../base/shared/table/paged-query-model';

@Injectable()
export class CategoryService {
  constructor(
    private categoryClient: CategoryClient,
    private toastService: ToastService,
    private spinnerService: SpinnerService
  ) { }

  public getData(pagedQuery: PagedQueryModel): Observable<IPagedItems> {
    this.spinnerService.showSpinner = true;
    return this.categoryClient
      .searchCategories(
        pagedQuery.filters,
        pagedQuery.skip,
        pagedQuery.take,
        pagedQuery.sortBy,
        pagedQuery.sortDirection
      )
      .pipe(
        tap(() => {
          this.spinnerService.showSpinner = false;
        }),
        catchError((error: any) => {
          this.spinnerService.showSpinner = false;
          if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
            this.toastService.showError(
              'i18n.errors.server-error.title',
              'i18n.errors.server-error.message',
              null,
              ErrorType.serverError
            );
          }
          return throwError(() => error);
        })
      );
  }

  public updateRecord(data: UpdateCategoryDTO): Observable<CategoryDTO> {
    this.spinnerService.showSpinner = true;
    return this.categoryClient.updateCategory(data.categoryId, data).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.category.messages.update-title',
          'i18n.domain.category.messages.update-success-message'
        );
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  public addRecord(data: CreateCategoryDTO): Observable<CategoryDTO> {
    this.spinnerService.showSpinner = true;
    return this.categoryClient.createCategory(data).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.category.messages.add-title',
          'i18n.domain.category.messages.add-success-message'
        );
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }

  public deleteRecord(id: number): Observable<any> {
    this.spinnerService.showSpinner = true;
    return this.categoryClient.deleteCategory(id).pipe(
      tap(() => {
        this.spinnerService.showSpinner = false;
        this.toastService.showSuccess(
          'i18n.domain.category.messages.delete-title',
          'i18n.domain.category.messages.delete-success-message'
        );
      }),
      catchError((error: any) => {
        this.spinnerService.showSpinner = false;
        if (!this.toastService.doesErrorExistAlready(ErrorType.serverError)) {
          this.toastService.showError(
            'i18n.errors.server-error.title',
            'i18n.errors.server-error.message',
            null,
            ErrorType.serverError
          );
        }
        return throwError(() => error);
      })
    );
  }
}
